/**
 * DavisonFeMnPage — Davison 1993 Fe/Mn redox cycling in anoxic hypolimnia
 *
 * Pseudo-first-order oxidation kinetics (Stumm & Morgan 1996):
 *   r_Fe = k_Fe · [O2] · [OH-]² · [Fe(II)]        k_Fe ≈ 1.5 × 10¹³ M⁻² atm⁻¹ s⁻¹
 *   r_Mn = k_Mn · [O2] · [OH-]² · [Mn(II)]        k_Mn ≈ 7  × 10¹¹ M⁻² atm⁻¹ s⁻¹
 *
 * → Fe(II) half-life is ~1-2 minutes at pH 8, DO 8 mg/L
 * → Mn(II) half-life is ~hours-days under same conditions — Mn is the much harder problem
 *
 * Reference: Davison W. (1993) Earth-Science Reviews 34(2): 119-163, Lancaster University.
 *            Stumm & Morgan (1996) Aquatic Chemistry, 3rd ed. Wiley.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Magnet, ArrowLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

const DWI_MN_UG_L = 50;
const DWI_FE_UG_L = 200;

export default function DavisonFeMnPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('davison_fe_mn');
  const [pH, setPH] = useState(7.2);
  const [doMgL, setDo] = useState(0.5);          // mg/L (anoxic hypolimnion)
  const [feII, setFe] = useState(1.2);           // mg/L dissolved Fe(II)
  const [mnII, setMn] = useState(0.85);          // mg/L dissolved Mn(II)
  const [tempC, setTempC] = useState(8);

  const R = useMemo(() => {
    const k_Fe = 1.5e13;
    const k_Mn = 7e11;
    // Concentrations
    const oh = Math.pow(10, pH - 14);
    const o2_atm = (doMgL / 8.6) * 0.21;   // approximate partial pressure equivalent
    // Pseudo-first-order rate constants
    const k1_Fe = k_Fe * o2_atm * oh * oh;  // 1/s
    const k1_Mn = k_Mn * o2_atm * oh * oh;  // 1/s
    // Half-lives
    const t12_Fe_s = Math.log(2) / Math.max(k1_Fe, 1e-12);
    const t12_Mn_s = Math.log(2) / Math.max(k1_Mn, 1e-12);
    // Convert to readable units
    const t12_Fe_min = t12_Fe_s / 60;
    const t12_Mn_h = t12_Mn_s / 3600;
    // After 30 days under current ORP (assume 50% Fe / Mn reduced from sediment per month)
    // — illustrative steady-state in 5-m hypolimnion
    const breachFe = (feII * 1000) > DWI_FE_UG_L;
    const breachMn = (mnII * 1000) > DWI_MN_UG_L;
    const breachFactorMn = (mnII * 1000) / DWI_MN_UG_L;
    return { t12_Fe_min, t12_Mn_h, breachFe, breachMn, breachFactorMn };
  }, [pH, doMgL, feII, mnII, tempC]);

  const fmt = (n: number, d = 1) => isFinite(n) ? n.toFixed(d) : '—';

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={Magnet} title="Module — Davison Fe-Mn Redox Cycling"
        subtitle="Hypolimnetic Fe(II) / Mn(II) accumulation & WTW Mn risk"
        actions={<div className="flex items-center gap-2 flex-wrap">
          <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'}`}>
            <input type="checkbox" checked={enabled} onChange={() => toggle('davison_fe_mn')} className="accent-emerald-500" /> Module {enabled ? 'ENABLED' : 'DISABLED'}
          </label>
          <button onClick={() => navigate('/modules')} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700"><ArrowLeft className="w-4 h-4" /> Modules</button>
        </div>}
      />
      <ModuleConflictBanner moduleId="davison_fe_mn" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Fe(II) half-life</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(R.t12_Fe_min, 1)} min</div>
          <div className="text-xs text-slate-500 mt-1">at pH {pH}, DO {doMgL} mg/L</div>
        </div>
        <div className="rounded-xl border-2 border-amber-700 bg-amber-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-amber-300">Mn(II) half-life</div>
          <div className="text-2xl font-bold text-amber-200 mt-1">{fmt(R.t12_Mn_h, 1)} h</div>
          <div className="text-xs text-slate-500 mt-1">Mn is the slow problem</div>
        </div>
        <div className={`rounded-xl border-2 p-4 ${R.breachFe ? 'border-red-700 bg-red-900/20' : 'border-emerald-700 bg-emerald-900/15'}`}>
          <div className="text-xs uppercase tracking-wider text-slate-300">Fe vs DWI 200 µg/L</div>
          <div className={`text-lg font-bold mt-1 ${R.breachFe ? 'text-red-200' : 'text-emerald-200'}`}>{(feII * 1000).toFixed(0)} µg/L {R.breachFe ? '— BREACH' : '— OK'}</div>
          <div className="text-xs text-slate-500 mt-1">aesthetic limit</div>
        </div>
        <div className={`rounded-xl border-2 p-4 ${R.breachMn ? 'border-red-700 bg-red-900/20' : 'border-emerald-700 bg-emerald-900/15'}`}>
          <div className="text-xs uppercase tracking-wider text-slate-300">Mn vs DWI 50 µg/L</div>
          <div className={`text-lg font-bold mt-1 ${R.breachMn ? 'text-red-200' : 'text-emerald-200'}`}>{(mnII * 1000).toFixed(0)} µg/L {R.breachMn ? `BREACH × ${R.breachFactorMn.toFixed(1)}` : 'OK'}</div>
          <div className="text-xs text-slate-500 mt-1">PCV — strict</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-5 gap-3 text-xs">
        <div>
          <label className="text-slate-400 block mb-1">pH: <strong className="text-slate-100">{pH.toFixed(1)}</strong></label>
          <input type="range" min={6.0} max={9.0} step={0.1} value={pH} onChange={e => setPH(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">DO: <strong className="text-slate-100">{doMgL.toFixed(2)} mg/L</strong></label>
          <input type="range" min={0.1} max={12} step={0.1} value={doMgL} onChange={e => setDo(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">Fe(II): <strong className="text-slate-100">{feII.toFixed(2)} mg/L</strong></label>
          <input type="range" min={0} max={5} step={0.05} value={feII} onChange={e => setFe(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">Mn(II): <strong className="text-slate-100">{mnII.toFixed(2)} mg/L</strong></label>
          <input type="range" min={0} max={3} step={0.02} value={mnII} onChange={e => setMn(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">Bed T: <strong className="text-slate-100">{tempC} °C</strong></label>
          <input type="range" min={2} max={22} step={0.5} value={tempC} onChange={e => setTempC(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2"><Info className="w-4 h-4 text-rb-brand" /> Water treatment relevance</div>
        <ul className="list-disc ml-5 space-y-1">
          <li>Reduced Fe(II) and Mn(II) accumulate in anoxic hypolimnia to mg/L levels — invisible until autumn overturn.</li>
          <li>At overturn the reduced metals enter the surface water and the abstraction column → WTW must treat them.</li>
          <li>Manganese is the killer: 50 µg/L PCV (DWI 2016 regulations), causes black stains in plumbing, slow oxidation kinetics.</li>
          <li>Aeration prevents the accumulation: keeping DO &gt; 4 mg/L at the sediment-water interface drives oxidation faster than reduction can re-supply.</li>
          <li>Without aeration: typical 6-week storage reservoir accumulates 1-3 mg/L Mn in the hypolimnion by August.</li>
        </ul>
        <p className="mt-2">The Redox-P-release module describes the parallel P-release driven by the same Fe(III)→Fe(II) reduction. The two
        modules are physically coupled — running both gives a unified internal-loading picture (P + Fe + Mn).</p>
        <p className="italic text-slate-500 mt-2">Reference: Davison W. (1993) Earth-Science Reviews 34: 119-163, Lancaster; Stumm &amp; Morgan (1996) Aquatic Chemistry, Wiley.</p>
      </div>
    </div>
  );
}
